import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  Inject,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import * as L from 'leaflet';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { BuildingService } from '../../services/building.service';

export interface MapSearchResult {
  lat: number;
  lon: number;
  buffer: number;
}

@Component({
  selector: 'app-building-map-search-dialog',
  templateUrl: './building-map-search-dialog.component.html',
  styleUrls: ['./building-map-search-dialog.component.scss'],
})
export class BuildingMapSearchDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  addressControl = new FormControl('');

  selectedLat: number | null = null;
  selectedLon: number | null = null;
  bufferMeters: number = 500;
  bufferInput: number = 500;

  geocodeResults: any[] = [];
  geocodeLoading = false;

  searchResults: any[] = [];
  searchLoading = false;
  hasSearched = false;
  clickedBuilding: any | null = null;

  private map!: L.Map;
  private marker: L.Marker | null = null;
  private bufferCircle: L.Circle | null = null;
  private resultMarkerLayer: L.LayerGroup | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<BuildingMapSearchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Partial<MapSearchResult>,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private buildingService: BuildingService,
  ) {}

  ngOnInit(): void {
    if (this.data?.lat) this.selectedLat = this.data.lat;
    if (this.data?.lon) this.selectedLon = this.data.lon;
    if (this.data?.buffer) {
      this.bufferMeters = this.data.buffer;
      this.bufferInput = this.data.buffer;
    }

    this.addressControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((val) => {
        if (val && val.trim().length >= 3) {
          this.geocode(val.trim());
        } else {
          this.geocodeResults = [];
        }
      });
  }

  ngAfterViewInit(): void {
    // Fix icona marker con bundler (deve essere impostato PRIMA della creazione della mappa)
    const iconDefault = L.icon({
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });
    L.Marker.prototype.options.icon = iconDefault;

    // Inizializziamo la mappa dopo l'apertura del dialog con double rAF:
    // il primo rAF garantisce che il browser abbia processato tutti i
    // cambiamenti di stile post-animazione; il secondo che abbia eseguito
    // il layout e il paint definitivi.
    this.dialogRef.afterOpened().subscribe(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.initMap();
        });
      });
    });
  }

  private initMap(): void {
    const centerLat = this.selectedLat ?? 41.9028;
    const centerLon = this.selectedLon ?? 12.4964;
    const zoom = this.selectedLat ? 14 : 6;

    this.map = L.map(this.mapContainer.nativeElement, {
      center: [centerLat, centerLon],
      zoom,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(this.map);

    if (this.selectedLat !== null && this.selectedLon !== null) {
      this.placeMarker(this.selectedLat, this.selectedLon);
    }

    this.map.on('click', (e: L.LeafletMouseEvent) => {
      if (this.searchResults.length === 0) {
        this.placeMarker(e.latlng.lat, e.latlng.lng);
      }
    });

    // Forza Leaflet a ricalcolare le dimensioni dopo il render completo
    this.map.invalidateSize({ animate: false });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.map) {
      this.map.remove();
    }
  }

  private placeMarker(lat: number, lon: number): void {
    this.selectedLat = lat;
    this.selectedLon = lon;

    if (this.marker) {
      this.marker.setLatLng([lat, lon]);
    } else {
      this.marker = L.marker([lat, lon]).addTo(this.map);
    }

    this.updateBufferCircle();
    this.map.panTo([lat, lon]);
  }

  private updateBufferCircle(): void {
    if (this.selectedLat === null || this.selectedLon === null) return;

    const radius = this.bufferMeters || 1;

    if (this.bufferCircle) {
      this.bufferCircle.setLatLng([this.selectedLat, this.selectedLon]);
      this.bufferCircle.setRadius(radius);
    } else {
      this.bufferCircle = L.circle([this.selectedLat, this.selectedLon], {
        radius,
        color: '#293683',
        fillColor: '#293683',
        fillOpacity: 0.15,
        weight: 2,
      }).addTo(this.map);
    }
  }

  private geocode(query: string): void {
    this.geocodeLoading = true;
    this.http
      .get<any[]>('https://nominatim.openstreetmap.org/search', {
        params: {
          q: query,
          format: 'json',
          limit: '5',
          addressdetails: '1',
        },
        headers: { 'Accept-Language': 'it' },
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.geocodeLoading = false;
          this.geocodeResults = results ?? [];
        },
        error: () => {
          this.geocodeLoading = false;
          this.geocodeResults = [];
        },
      });
  }

  selectGeocodeResult(result: any): void {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    this.addressControl.setValue(result.display_name, { emitEvent: false });
    this.geocodeResults = [];
    this.placeMarker(lat, lon);
    this.map.setView([lat, lon], 15);
  }

  onBufferSliderChange(value: number): void {
    this.bufferMeters = value;
    this.bufferInput = value;
    this.updateBufferCircle();
    this.cdr.detectChanges();
  }

  onBufferInputChange(value: number): void {
    const clamped = Math.max(50, Math.min(50000, value || 50));
    this.bufferMeters = clamped;
    this.bufferInput = clamped;
    this.updateBufferCircle();
    this.cdr.detectChanges();
  }

  get canSearch(): boolean {
    return this.selectedLat !== null && this.selectedLon !== null && (this.bufferMeters ?? 0) > 0;
  }

  search(): void {
    if (!this.canSearch) return;
    this.searchLoading = true;
    this.hasSearched = false;
    this.clickedBuilding = null;
    this.clearResultMarkers();
    this.cdr.detectChanges();

    const params = {
      lat: this.selectedLat!,
      lon: this.selectedLon!,
      buffer: this.bufferMeters,
    };

    this.buildingService.extendedSearch(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results: any[]) => {
          this.searchLoading = false;
          this.hasSearched = true;
          this.searchResults = results || [];
          this.plotResultMarkers();
          this.cdr.detectChanges();
        },
        error: () => {
          this.searchLoading = false;
          this.hasSearched = true;
          this.searchResults = [];
          this.cdr.detectChanges();
        },
      });
  }

  private plotResultMarkers(): void {
    this.clearResultMarkers();
    this.resultMarkerLayer = L.layerGroup().addTo(this.map);

    const buildingIcon = L.divIcon({
      className: '',
      html: `<div class="result-pin"></div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
      popupAnchor: [0, -12],
    });

    for (const building of this.searchResults) {
      if (!building.coordY || !building.coordX) continue;
      const m = L.marker([building.coordY, building.coordX], { icon: buildingIcon });
      const label = [building.name, building.streetName, building.streetNumber, building.city]
        .filter(Boolean).join(' - ');
      m.bindTooltip(label, { sticky: true, className: 'building-tooltip' });
      m.on('click', () => {
        this.clickedBuilding = building;
        this.cdr.detectChanges();
      });
      m.addTo(this.resultMarkerLayer!);
    }

    const withCoords = this.searchResults.filter(b => b.coordY && b.coordX);
    if (withCoords.length > 0) {
      const bounds = L.latLngBounds(withCoords.map(b => [b.coordY, b.coordX] as [number, number]));
      this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  }

  private clearResultMarkers(): void {
    if (this.resultMarkerLayer) {
      this.resultMarkerLayer.clearLayers();
      this.map.removeLayer(this.resultMarkerLayer);
      this.resultMarkerLayer = null;
    }
  }

  resetSearch(): void {
    this.searchResults = [];
    this.hasSearched = false;
    this.clickedBuilding = null;
    this.clearResultMarkers();
    if (this.selectedLat !== null && this.selectedLon !== null) {
      this.map.setView([this.selectedLat, this.selectedLon], 14);
    }
    this.cdr.detectChanges();
  }

  selectBuilding(): void {
    if (!this.clickedBuilding) return;
    this.dialogRef.close(this.clickedBuilding);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
